import {ToggleOff, ToggleOn} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';

interface Props {
  pk?: number;
  name?: string;
  onClose?: () => void;
}

const SitePermissionsEdit = ({pk, name, onClose}: Props) => {
  // Fetch
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  // Need to fetch list of users and any permissions for the specified site that currently exist
  const fetchData = async () => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get(`${apiBaseUrl}/site-permissions/${pk}`);
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_7592c701');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePermissionsChange = async (user: any) => {
    // if permission doesn't exist, create it
    // permission should be allowed=false
    if (!user.permissions_id) {
      try {
        await API.post(`${apiBaseUrl}/site-permissions`, {
          user_id: user.id,
          site_id: pk,
          allowed: false,
        });
        fetchData();
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_34571bd3');
        setFetchedErrors(messages);
      }
    } else {
      // else change it from it's current value
      try {
        await API.patch(
          `${apiBaseUrl}/site-permissions/${user.permissions_id}`,
          {
            allowed: !user.allowed || false,
          }
        );
        fetchData();
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_34571bd5');
        setFetchedErrors(messages);
      }
    }
  };

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      position="relative"
      gap={3}
      p={3}
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" width="100%" gap={1.5}>
          <Box fontSize={24}>Site Access Permissions - {name}</Box>
        </Box>
        {onClose ? (
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}{' '}
        </Alert>
      ))}

      {fetchedData.map((u, i) => {
        return (
          <Box key={i} display="flex" flexDirection="row">
            <Box
              flex={1}
              display="flex"
              justifyContent="flex-start"
              textAlign="left"
              padding={0}
            >
              {u.name}
            </Box>
            <Box
              flex={1}
              display="flex"
              justifyContent="flex-start"
              textAlign="left"
              padding={0}
            >
              {u.email}
            </Box>
            <Box
              flex={1}
              display="flex"
              justifyContent="flex-end"
              textAlign="right"
              padding={0}
            >
              {u.allowed ? (
                <ToggleOn
                  color="success"
                  onClick={() => handlePermissionsChange(u)}
                ></ToggleOn>
              ) : (
                <ToggleOff
                  color="error"
                  onClick={() => handlePermissionsChange(u)}
                ></ToggleOff>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SitePermissionsEdit;
